import { ref, type Directive, type Ref } from 'vue';
import {
  hasAttrInEventPath,
  recordSelected,
} from '@/components/share/fileExplorer.script';
import { useLayout } from '@/store/layout';
import { isMacOS } from '@/utils';
import _ from 'lodash';
import { useShare } from '~/store/share';

/* 使用指令的方式绑定与选中文件相关的操作 */
export default ({
  recordSelectIds,
  findFileIndexById,
}: {
  recordSelectIds(needCompare?: boolean): void;
  findFileIndexById(id: string): number;
}): Directive<HTMLElement, PPFile> => {
  // 在移动端，长按 500ms 时，进入选择模式，如果不足 500ms，取消
  let touchstartTimeout: ReturnType<typeof setTimeout>;
  const layoutStore = useLayout();
  const shareStore = useShare();
  const mouseState = ref({
    down: false,
    id: '',
  });

  /* 单击的处理程序 */
  const handleClick = (file: PPFile, e: MouseEvent) => {
    const hasPressCtrlKey = isMacOS() ? e.metaKey : e.ctrlKey;
    const currentIndex = findFileIndexById(file.id);

    if (hasPressCtrlKey || hasAttrInEventPath(e, 'data-file-toggle-event')) {
      recordSelected.push(currentIndex); // 多选
    } else if (e.shiftKey) {
      recordSelected.selectRange(currentIndex); // 跨选
    } else {
      recordSelected.setPath(currentIndex); // 单选
    }
    recordSelectIds();
  };

  const click = (file: PPFile) => (e: MouseEvent) => {
    if (layoutStore.isMobile) {
      return shareStore.setSelectedIds(
        _.xor(shareStore.selectedIds, [file.id]),
      );
    }
    if (isMacOS() ? e.metaKey : e.ctrlKey) return;
    handleClick(file, e);
  };

  // 按住ctrl键时，使用 mousedown和mouseup实现click效果
  const mousedown = (file: PPFile) => (e: MouseEvent) => {
    if (!(isMacOS() ? e.metaKey : e.ctrlKey)) return;
    mouseState.value = {
      down: true,
      id: file.id,
    };
  };

  const mouseup = (file: PPFile) => (e: MouseEvent) => {
    if (!(isMacOS() ? e.metaKey : e.ctrlKey)) return;
    if (!mouseState.value.down || mouseState.value.id !== file.id) return;
    handleClick(file, e);
    mouseState.value = { down: false, id: '' };
  };

  // region 移动端的事件
  const touchstart = (file: PPFile) => () => {
    // if (route.name === RouteName.SHARED && route.params.id) return false // 查看分享文件一级目录，禁止操作
    if (shareStore.mobileSelectMode || !layoutStore.isMobile) return;
    clearTimeout(touchstartTimeout);
    touchstartTimeout = setTimeout(() => {
      shareStore.mobileSelectMode = true;
      shareStore.setSelectedIds([file.id]);
    }, 500);
  };
  const touchmove = () => clearTimeout(touchstartTimeout);
  const touchend = () => clearTimeout(touchstartTimeout);
  // endregion

  return {
    mounted(el: HTMLElement, binding: Ref<PPFile>) {
      const file = binding.value;
      el.addEventListener('click', click(file));
      el.addEventListener('mousedown', mousedown(file));
      el.addEventListener('mouseup', mouseup(file));
      el.addEventListener('touchstart', touchstart(file));
      el.addEventListener('touchmove', touchmove);
      el.addEventListener('touchend', touchend);
    },
    unmounted(el: HTMLElement, binding: Ref<PPFile>) {
      const file = binding.value;
      el.removeEventListener('click', click(file));
      el.removeEventListener('mousedown', mousedown(file));
      el.removeEventListener('mouseup', mouseup(file));
      el.removeEventListener('touchstart', touchstart(file));
      el.removeEventListener('touchmove', touchmove);
      el.removeEventListener('touchend', touchend);
    },
  };
};
